// frontend/src/components/Provider/ProviderCard.tsx
// CLEANED VERSION - No selection features, mobile-first design

import React from 'react';
import { 
  Eye, 
  Edit, 
  Trash2, 
  Shield, 
  Star,
  MapPin,
  Phone,
  Mail,
  CheckCircle,
  XCircle,
  Play,
  Pause
} from 'lucide-react';
import { Provider } from '@/lib/api/providerService';
import { ActionsDropdown, createCommonActions } from '@/components/common/ui';

interface ProviderCardProps {
  provider: Provider;
  onView: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onVerify: () => void;
  onToggleStatus: () => void;
}

const ProviderCard: React.FC<ProviderCardProps> = ({
  provider,
  onView,
  onEdit,
  onDelete,
  onVerify,
  onToggleStatus
}) => {
  const renderStars = (rating: number) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <Star key={i} className="h-3 w-3 text-yellow-400 fill-current" />
      );
    }

    if (hasHalfStar) {
      stars.push(
        <Star key="half" className="h-3 w-3 text-yellow-400 fill-current opacity-50" />
      );
    }

    const remainingStars = 5 - Math.ceil(rating);
    for (let i = 0; i < remainingStars; i++) {
      stars.push(
        <Star key={`empty-${i}`} className="h-3 w-3 text-gray-300" />
      );
    }

    return stars;
  };

  return (
    <div className="px-4 py-4 sm:px-6 hover:bg-gray-50 transition-colors">
      <div className="flex items-center justify-between">
        {/* Provider Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              {/* Business Name & Status */}
              <div className="flex items-center space-x-2 mb-2">
                <h3 className="text-lg font-medium text-gray-900 truncate">
                  {provider.business_name || 'Unnamed Business'}
                </h3>
                <div className="flex space-x-1">
                  {provider.is_verified && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Verified
                    </span>
                  )}
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    provider.is_active 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {provider.is_active ? (
                      <>
                        <Play className="h-3 w-3 mr-1" />
                        Active
                      </>
                    ) : (
                      <>
                        <Pause className="h-3 w-3 mr-1" />
                        Inactive
                      </>
                    )}
                  </span>
                </div>
              </div>

              {/* Contact Info */}
              <div className="flex flex-col sm:flex-row sm:items-center space-y-1 sm:space-y-0 sm:space-x-4 text-sm text-gray-500 mb-2">
                {provider.email && (
                  <div className="flex items-center">
                    <Mail className="h-4 w-4 mr-1" />
                    <span className="truncate">{provider.email}</span>
                  </div>
                )}
                {provider.phone && (
                  <div className="flex items-center">
                    <Phone className="h-4 w-4 mr-1" />
                    <span>{provider.phone}</span>
                  </div>
                )}
                {provider.city && (
                  <div className="flex items-center">
                    <MapPin className="h-4 w-4 mr-1" />
                    <span>{provider.city}</span>
                  </div>
                )}
              </div>

              {/* Rating & Stats */}
              <div className="flex items-center space-x-4 text-sm text-gray-500">
                {provider.average_rating > 0 && (
                  <div className="flex items-center">
                    <div className="flex mr-1">
                      {renderStars(provider.average_rating)}
                    </div>
                    <span>
                      {provider.average_rating.toFixed(1)} ({provider.total_reviews || 0} reviews)
                    </span>
                  </div>
                )}
                <span>{provider.total_services || 0} services</span>
                <span>{provider.total_bookings || 0} bookings</span>
              </div>
            </div>

            {/* Actions Menu */}
            <div className="ml-4">
              <ActionsDropdown
                size="md"
                actions={[
                  createCommonActions.view(onView),
                  createCommonActions.edit(onEdit),
                  ...(!provider.is_verified ? [{
                    id: 'verify',
                    label: 'Verify Provider',
                    icon: Shield,
                    onClick: onVerify,
                    variant: 'success' as const
                  }] : []),
                  createCommonActions.toggleStatus(
                    provider.is_active,
                    onToggleStatus
                  ),
                  createCommonActions.delete(onDelete)
                ]}
              />
            </div>
          </div>

          {/* Description */}
          {provider.description && (
            <p className="mt-2 text-sm text-gray-600 line-clamp-2">
              {provider.description}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProviderCard;